/**
 * Chamadas HTTP dos cadastros da coluna: a escala da casa, os prestadores
 * esporádicos e a escala padrão.
 *
 * Papel: aqui ficam as chamadas de `/api/calendario/equipe`,
 * `/api/calendario/prestadores` e `/api/calendario/escala-padrao`.
 *
 * Como usar: os três são da casa inteira, e não de um período, e por isso pedem
 * só `residenciaId`. Tirar alguém da escala ou da lista de prestadores não mexe
 * em dia, férias nem diária já gravados.
 */
import {
  type EscalaPadraoDoDia,
  type MembroDaEquipe,
  type NovoMembroDaEquipe,
  type NovoPrestador,
  type Prestador,
  escalaPadraoDoDiaSchema,
  membroDaEquipeSchema,
  prestadorSchema,
} from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

const listaDaEquipeSchema = z.array(membroDaEquipeSchema);
const listaDePrestadoresSchema = z.array(prestadorSchema);
const listaDaEscalaPadraoSchema = z.array(escalaPadraoDoDiaSchema);

/**
 * Lê quem está na escala da casa, na ordem em que entrou nela.
 *
 * @param residenciaId - A casa do calendário aberto.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As pessoas da escala, cada uma com o colaborador aninhado.
 */
export function buscarEquipe(
  residenciaId: string,
  sinal?: AbortSignal,
): Promise<MembroDaEquipe[]> {
  return clienteApi.get('/calendario/equipe', {
    schema: listaDaEquipeSchema,
    params: { residenciaId },
    sinal,
  });
}

/**
 * Põe uma pessoa do cadastro de pessoal na escala da casa.
 *
 * @param dados - A casa e o colaborador; a API responde 409 quando a pessoa já
 *   está na escala.
 * @returns A pessoa como entrou na escala.
 */
export function incluirNaEquipe(dados: NovoMembroDaEquipe): Promise<MembroDaEquipe> {
  return clienteApi.post('/calendario/equipe', dados, { schema: membroDaEquipeSchema });
}

/**
 * Tira uma pessoa da escala da casa, sem apagar os dias em que ela foi escalada.
 *
 * @param id - Identificador da linha em `calendario_equipe`, e não o do
 *   colaborador.
 */
export function tirarDaEquipe(id: string): Promise<void> {
  return clienteApi.remover(`/calendario/equipe/${encodeURIComponent(id)}`);
}

/**
 * Lê os prestadores esporádicos da casa, na ordem em que entraram na lista.
 *
 * @param residenciaId - A casa do calendário aberto.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns Os prestadores.
 */
export function buscarPrestadores(
  residenciaId: string,
  sinal?: AbortSignal,
): Promise<Prestador[]> {
  return clienteApi.get('/calendario/prestadores', {
    schema: listaDePrestadoresSchema,
    params: { residenciaId },
    sinal,
  });
}

/**
 * Cadastra um prestador esporádico.
 *
 * @param dados - A casa e o nome; a API responde 409 quando o nome já está na
 *   lista.
 * @returns O prestador como ficou gravado.
 */
export function cadastrarPrestador(dados: NovoPrestador): Promise<Prestador> {
  return clienteApi.post('/calendario/prestadores', dados, { schema: prestadorSchema });
}

/**
 * Tira um prestador da lista, sem mexer nas diárias que já têm o nome dele.
 *
 * @param id - Identificador da linha em `calendario_prestadores`.
 */
export function removerPrestador(id: string): Promise<void> {
  return clienteApi.remover(`/calendario/prestadores/${encodeURIComponent(id)}`);
}

/**
 * Lê a escala padrão de cada dia da semana da casa.
 *
 * @param residenciaId - A casa do calendário aberto.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns Um item por dia da semana que tem padrão; o dia sem item não tem.
 */
export function buscarEscalaPadrao(
  residenciaId: string,
  sinal?: AbortSignal,
): Promise<EscalaPadraoDoDia[]> {
  return clienteApi.get('/calendario/escala-padrao', {
    schema: listaDaEscalaPadraoSchema,
    params: { residenciaId },
    sinal,
  });
}
